import {Injectable} from '@angular/core';
import {BehaviorSubject, from, map, Observable, of, throwError} from "rxjs";

import {ComuneCittadinoSummaryDTO, LoginResponse, UserProfileDTO} from "../core/api/generated";
import {JsBridgeService} from "./js-bridge.service";
import {AuthMode} from "../shared/enums/auth-mode.enum";
import {JwtIdentity, ProfileFormClaims, IdentityProfile} from "../shared/interfaces/jwt-identity.interface";
import {Anagrafica} from "../shared/interfaces/studente-figlio.interface";
import {emailRegex} from "../shared/constants/regex";


@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private readonly _checkLoggedUser$ = new BehaviorSubject(!!this.getAccessToken());
  readonly checkLoggedUser$: Observable<boolean> = this._checkLoggedUser$.asObservable();

  private readonly _checkUpdateProfile$ = new BehaviorSubject(!!this.getProfile());
  readonly checkUpdateProfile$: Observable<boolean> = this._checkUpdateProfile$.asObservable();

  currentToken: any | null = null;

  profile: IdentityProfile | undefined;
  userProfile: UserProfileDTO | null | undefined;
  anagrafiche: Anagrafica[] | undefined;
  refreshToken: string | null | undefined;
  accessToken: string | undefined | null;
  spidAccessToken: string | null = null;
  authMode: AuthMode | null = null;
  isMobilePlatform = false;
  deviceId: string | null = null;
  constructor(private jsBridgeService: JsBridgeService) {
    this.migrateLegacyTokensFromLocalStorage();
    this.restoreFromSessionStorage();
  }

  /**
   * Migrazione one-time: versioni precedenti scrivevano i token in localStorage
   * (tramite il bridge nativo). Li spostiamo in sessionStorage una sola volta per
   * non forzare il logout degli utenti già loggati al primo avvio post-deploy.
   */
  private migrateLegacyTokensFromLocalStorage() {
    const sensitiveKeys = [
      StorageProperties.ACCESS_TOKEN,
      StorageProperties.REFRESH_TOKEN,
      StorageProperties.SPID_ACCESS_TOKEN,
      StorageProperties.AUTH_MODE,
    ];
    for (const key of sensitiveKeys) {
      const legacy = localStorage.getItem(key);
      if (legacy !== null) {
        if (sessionStorage.getItem(key) === null) {
          sessionStorage.setItem(key, legacy);
        }
        localStorage.removeItem(key);
      }
    }
  }

  private restoreFromSessionStorage() {
    const at = sessionStorage.getItem(StorageProperties.ACCESS_TOKEN);
    const rt = sessionStorage.getItem(StorageProperties.REFRESH_TOKEN);
    const spidAt = sessionStorage.getItem(StorageProperties.SPID_ACCESS_TOKEN);
    const am = sessionStorage.getItem(StorageProperties.AUTH_MODE);

    if (at) {
      this.accessToken = at;
      this._checkLoggedUser$.next(true);
    }
    if (rt) {
      this.refreshToken = rt;
    }
    if (spidAt) {
      this.spidAccessToken = spidAt;
    }
    if (am) {
      this.authMode = am as AuthMode;
    }
  }
  setDeviceId(id: string){
    this.deviceId = id;
  }
  //// ------ GET

  getAccessToken(): string | undefined | null {
    return this.accessToken;
  }

  getRefreshToken(): string | undefined | null {
    return this.refreshToken
  }

  getProfile(): IdentityProfile | undefined {
    return this.profile;
  }

  getUserProfile(): UserProfileDTO | null | undefined {
    return this.userProfile;
  }

  getAnagrafiche(): Anagrafica[] | undefined {
    return this.anagrafiche;
  }

  decodeClaims(): JwtIdentity | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }
      const payload = parts[1];
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '==='.slice((normalized.length + 3) % 4);
      // `atob` restituisce un byte per carattere: il payload è UTF-8, quindi va ridecodificato,
      // altrimenti "Troè" diventa "TroÃ¨" in nome/cognome (e nell'autofill della pratica).
      const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
      const claims = JSON.parse(new TextDecoder().decode(bytes));

      const id: string | undefined = claims.sub;
      // `preferred_username` è un identificativo di login Keycloak, NON necessariamente un'email:
      // con SPID/CIE vale `tinit-<codicefiscale>`. Lo si accetta come email solo se il token non
      // porta un `email` claim E il valore ha davvero forma di email; altrimenti `email` resta
      // undefined (il campo email della pratica va compilato dall'utente, non prefillato con un
      // pseudo-indirizzo che il BE rifiuterebbe / a cui non si può inviare l'OTP).
      const preferredAsEmail =
        typeof claims.preferred_username === 'string' &&
        new RegExp(emailRegex).test(claims.preferred_username)
          ? claims.preferred_username
          : undefined;
      const email: string | undefined = claims.email ?? preferredAsEmail;
      const nome: string | undefined = claims.given_name;
      const cognome: string | undefined = claims.family_name;
      const computedFullname = [nome, cognome].filter((s) => !!s).join(' ').trim();
      const fullname: string | undefined = claims.name ?? (computedFullname || undefined);
      const codiceFiscale: string | undefined =
        claims.fiscal_number ?? claims.codiceFiscale ?? claims.fiscalNumber ?? claims.cf;
      const roles: string[] = Array.isArray(claims?.realm_access?.roles)
        ? claims.realm_access.roles
        : [];

      return {id, email, nome, cognome, fullname, codiceFiscale, roles};
    } catch {
      return null;
    }
  }

  mapClaimsToProfileForm(): ProfileFormClaims {
    const claims = this.decodeClaims();
    return {
      nome: claims?.nome ?? '',
      cognome: claims?.cognome ?? '',
      codiceFiscale: (claims?.codiceFiscale ?? '').toUpperCase(),
    };
  }

  getPlatform() {
    return localStorage.getItem(StorageProperties.PLATFORM);
  }

  get isLogged() {
    return !!this.getAccessToken()
  }

  //----MARK: GENERIC ----//

  // generic get
  async checkPreferenceToken(key: StorageProperties) {
    return localStorage.getItem(key);
  }

  // generic set
  async setPreferenceToken(key: StorageProperties, value: string) {
    localStorage.setItem(key, value);
  }

  // generic clean/remove
  async removePreferenceToken(key: StorageProperties) {
    localStorage.removeItem(key);
  }

  //----ENDMARK: GENERIC ----//

  //----MARK: FUNZIONI DI SET ----//

  setPreferenceProfile$(user: IdentityProfile): Observable<IdentityProfile> {
    if (!!user) {
      this.profile = user;
      this._checkUpdateProfile$.next(!!user);
      return of(user);
    } else {
      return throwError(() => new Error)
    }
  }

  setAnagrafiche$(anagrafiche: Anagrafica[]): Observable<Anagrafica[]> {
    this.anagrafiche = anagrafiche;
    return of(anagrafiche);
  }

  setUserProfile$(p: UserProfileDTO | null): Observable<UserProfileDTO | null> {
    this.userProfile = p;
    return of(p);
  }

  setAccessToken$(value: string) {
    this.accessToken = value;
    sessionStorage.setItem(StorageProperties.ACCESS_TOKEN, value);
    this._checkLoggedUser$.next(!!value);
    return of(value);
  }

  setRefreshToken$(value: string) {
    this.refreshToken = value;
    sessionStorage.setItem(StorageProperties.REFRESH_TOKEN, value);
    return of(value);
  }

  setSpidAccessToken$(value: string) {
    this.spidAccessToken = value;
    sessionStorage.setItem(StorageProperties.SPID_ACCESS_TOKEN, value);
    return of(value);
  }

  setExpires$(value: number) {
    return of(value);
  }

  setAuth$(auth: LoginResponse) {
    return this.setToken$(auth.access_token!, auth.refresh_token!, auth.expires_in!);
  }

  setToken$(access_token: string, refresh_token: string, expiration: number) {
    this.accessToken = access_token;
    this.refreshToken = refresh_token;
    this._checkLoggedUser$.next(!!access_token);
    return of([access_token, refresh_token, expiration]);
  }

  setSlugComune$(slug: string): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE, slug));
  }

  getSlugComune$(): Observable<string | null> {
    return from(this.checkPreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE));
  }

  removeSlugComune$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.SLUG_COMUNE_PRATICHE));
  }

  setComuneAttivo$(comune: ComuneCittadinoSummaryDTO): Observable<void> {
    return from(
      this.setPreferenceToken(StorageProperties.COMUNE_ATTIVO, JSON.stringify(comune))
    );
  }

  getComuneAttivo$(): Observable<ComuneCittadinoSummaryDTO | null> {
    return from(this.checkPreferenceToken(StorageProperties.COMUNE_ATTIVO)).pipe(
      map((value) => {
        if (!value) return null;
        try {
          return JSON.parse(value) as ComuneCittadinoSummaryDTO;
        } catch {
          return null;
        }
      })
    );
  }

  removeComuneAttivo$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.COMUNE_ATTIVO));
  }

  //----MARK: SERVIZIO ATTIVO ----//
  // Persistenza dello slug del servizio attivo (gemello del comune attivo).
  // SERVIZIO_ATTIVO = servizio corrente nella sessione; ULTIMO_SERVIZIO = ultimo usato,
  // riaperto all'avvio quando non c'è un servizio attivo né un riferimento nell'URL.

  setServizioAttivo$(slug: string): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.SERVIZIO_ATTIVO, slug));
  }

  getServizioAttivo$(): Observable<string | null> {
    return from(this.checkPreferenceToken(StorageProperties.SERVIZIO_ATTIVO));
  }

  removeServizioAttivo$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.SERVIZIO_ATTIVO));
  }

  setUltimoServizio$(slug: string): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.ULTIMO_SERVIZIO, slug));
  }

  getUltimoServizio$(): Observable<string | null> {
    return from(this.checkPreferenceToken(StorageProperties.ULTIMO_SERVIZIO));
  }

  removeUltimoServizio$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.ULTIMO_SERVIZIO));
  }

  // Id dell'istanza mensa scelta dall'utente (asse ACCESSI): è il valore dell'header
  // Gestione-Mense-Servizio-Id quando il comune ha più istanze mensa attive. La scelta
  // "resta finché valida" (id ancora tra i servizi attivi del comune); l'auto-selezione
  // "più viva" resta il fallback. Vedi change select-servizio-post-accettazione.

  setIstanzaMensaSelezionata$(id: string): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.ISTANZA_MENSA_SELEZIONATA, id));
  }

  getIstanzaMensaSelezionata$(): Observable<string | null> {
    return from(this.checkPreferenceToken(StorageProperties.ISTANZA_MENSA_SELEZIONATA));
  }

  removeIstanzaMensaSelezionata$(): Observable<void> {
    return from(this.removePreferenceToken(StorageProperties.ISTANZA_MENSA_SELEZIONATA));
  }

  //----ENDMARK: SERVIZIO ATTIVO ----//

  //----MARK: SPID/PASSKEY ----//

  setAuthMode$(mode: AuthMode): Observable<void> {
    this.authMode = mode;
    sessionStorage.setItem(StorageProperties.AUTH_MODE, mode);
    return of(undefined);
  }

  getAuthMode(): AuthMode | null {
    return this.authMode;
  }

  setSpidAuth$(accessToken: string, refreshToken: string, expiresIn: number): Observable<any> {
    this.spidAccessToken = accessToken;
    this.refreshToken = refreshToken;
    sessionStorage.setItem(StorageProperties.SPID_ACCESS_TOKEN, accessToken);
    sessionStorage.setItem(StorageProperties.REFRESH_TOKEN, refreshToken);
    return of({accessToken, refreshToken, expiresIn});
  }

  getSpidAccessToken(): string | null {
    return this.spidAccessToken;
  }

  setPasskeyHint$(hint: {userId: string; displayName: string}): Observable<void> {
    return from(this.setPreferenceToken(StorageProperties.PASSKEY_HINT, JSON.stringify(hint)));
  }

  getPasskeyHint$(): Observable<{userId: string; displayName: string} | null> {
    return from(this.checkPreferenceToken(StorageProperties.PASSKEY_HINT)).pipe(
      map((value) => {
        if (!value) return null;
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      })
    );
  }

  //----ENDMARK: SPID/PASSKEY ----//

  //----ENDMARK: FUNZIONI DI SET ----//
  getUserId(): string | null {
    return this.profile?.id ?? null;
  }
  //----MARK: FUNZIONI DI CLEAN ----//
  cleanPreferenceToken$() {
    const accessToken = this.getAccessToken();
    const userId = this.getUserId();

    if (accessToken && userId) {
      this.jsBridgeService.logout({ accessToken, userId });
    }

    this.profile = undefined;
    this.userProfile = undefined;
    this.anagrafiche = undefined;
    this.accessToken = undefined;
    this.refreshToken = undefined;
    this.spidAccessToken = null;
    this.authMode = null;
    this._checkLoggedUser$.next(false);
    this._checkUpdateProfile$.next(false);

    sessionStorage.removeItem(StorageProperties.ACCESS_TOKEN);
    sessionStorage.removeItem(StorageProperties.REFRESH_TOKEN);
    sessionStorage.removeItem(StorageProperties.SPID_ACCESS_TOKEN);
    sessionStorage.removeItem(StorageProperties.AUTH_MODE);
    // Slot dell'identità SPID/CIE in attesa di collegamento: ogni teardown di sessione
    // (logout, 401, "Esci" dalla modale di collegamento) deve svuotarlo, altrimenti un reload
    // riproporrebbe la modale a un utente che è uscito.
    sessionStorage.removeItem(StorageProperties.COLLEGAMENTO_PENDING);

    return of(true);
  }

  //----ENDMARK: FUNZIONI DI CLEAN ----//
}

export enum StorageProperties {
  TOKEN_RESPONSE = 'TOKEN_RESPONSE',
  ACCESS_TOKEN = 'access_token',
  REFRESH_TOKEN = 'refresh_token',
  EXPIRATION = 'expiration',
  PROFILE = 'user',
  PLATFORM = 'platform',
  SLUG_COMUNE_PRATICHE = 'slug_comune_pratiche',
  COMUNE_ATTIVO = 'comune_attivo',
  SERVIZIO_ATTIVO = 'servizio_attivo',
  ULTIMO_SERVIZIO = 'ultimo_servizio',
  ISTANZA_MENSA_SELEZIONATA = 'istanza_mensa_selezionata',
  AUTH_MODE = 'auth_mode',
  SPID_ACCESS_TOKEN = 'spid_access_token',
  SPID_REFRESH_TOKEN = 'spid_refresh_token',
  SPID_EXPIRATION = 'spid_expiration',
  PASSKEY_HINT = 'passkey_hint',
  /** Token (+ alias IdP) dell'identità SPID/CIE in attesa di collegamento, solo in sessionStorage. */
  COLLEGAMENTO_PENDING = 'collegamento_pending',
  /**
   * Elenco degli utenti che hanno rifiutato la proposta di registrazione passkey ("Salta").
   * Il rifiuto è permanente: chi salta non riceve più la proposta automatica al login e può
   * registrare la passkey dal Profilo. Contiene id utente, NON un booleano globale: su un
   * dispositivo condiviso il rifiuto di uno non deve zittire la proposta per gli altri.
   */
  PASSKEY_PROMPT_RIFIUTATO = 'passkey_prompt_rifiutato',
  TEXT_SIZE = 'text_size',
  LANG = 'lang',
}

export enum UserRolesEnum {
  ADMIN = 'admin',
}

export interface TokenI {
  realm_access: RealmAccessI;
}

export interface RealmAccessI {
  roles: string[];
}

